import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { SpriteIcon } from '@/components/SpriteIcon';
import { AuthCard, AuthCardLinks, AuthErrorSummary } from './AuthCard';
import { AuthField } from './AuthField';
import { SubmitButton } from './SubmitButton';
import { newPasswordSchema, type NewPasswordValues } from './schemas';
import { useServerValidation } from './useServerValidation';

const FIELDS = ['newPassword', 'confirmPassword'] as const;

interface TokenPasswordScreenProps {
  /** `GET`, answering `{ valid: boolean }` for the link's userId/token pair. */
  validatePath: string;
  /** `POST`, taking userId, token and the new password. */
  submitPath: string;
  title: string;
  subtitle: string;
  /** Replaces the subtitle once the link turns out to be unusable. */
  invalidSubtitle: string;
  /** Body of the invalid-link alert; carries a link on the reset screen. */
  invalidMessage: ReactNode;
  newPasswordPlaceholder: string;
  confirmPasswordPlaceholder: string;
  submitIcon: string;
  submitLabel: string;
  submitVariant?: string;
  pendingLabel: string;
  /** Shown on the login screen afterwards, as the page models' TempData was. */
  successMessage: string;
}

/**
 * The shared body of the two emailed-link screens.
 *
 * `ResetPassword.cshtml` and `ActivateAccount.cshtml` are the same page twice:
 * same form, same hidden userId/token, same invalid-link branch — they differ in
 * their wording, the button colour, and which token provider the server checks
 * against. The two remain separate routes and separate endpoints; only the
 * markup is shared.
 *
 * The link is validated before the form is shown, exactly as `OnGetAsync` did,
 * so a link that has already been used says so instead of presenting a form that
 * is guaranteed to fail. Validation does not consume the token.
 */
export function TokenPasswordScreen({
  validatePath,
  submitPath,
  title,
  subtitle,
  invalidSubtitle,
  invalidMessage,
  newPasswordPlaceholder,
  confirmPasswordPlaceholder,
  submitIcon,
  submitLabel,
  submitVariant = 'primary',
  pendingLabel,
  successMessage,
}: TokenPasswordScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userId = searchParams.get('userId') ?? '';
  const token = searchParams.get('token') ?? '';

  const [linkState, setLinkState] = useState<'checking' | 'valid' | 'invalid'>('checking');

  useEffect(() => {
    if (!userId || !token) {
      setLinkState('invalid');
      return;
    }

    // Guards a late response from a superseded request, and any state update
    // after the user has navigated away.
    let active = true;

    api
      .get<{ valid: boolean }>(validatePath, { query: { userId, token } })
      .then((response) => {
        if (active) setLinkState(response.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (active) setLinkState('invalid');
      });

    return () => {
      active = false;
    };
  }, [userId, token, validatePath]);

  const schema = useMemo(() => newPasswordSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const { formErrors, clearFormErrors, applyServerErrors } = useServerValidation(setError, FIELDS);

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      await api.post<void>(submitPath, { userId, token, ...values });
      // The account is usable but not signed in — the Razor pages sent the user
      // to the login form with a confirmation, and so does this.
      navigate('/auth-login', { replace: true, state: { status: successMessage } });
    } catch (error) {
      applyServerErrors(error);
    }
  });

  if (linkState === 'checking') {
    return (
      <AuthCard title={title} subtitle={subtitle} footer={<AuthCardLinks />}>
        <div className="text-center py-3">
          <span className="spinner-border text-primary" role="status" aria-hidden="true" />
          <p className="text-muted mt-3 mb-0">{t('auth:Auth.CheckingLink', 'Checking your link...')}</p>
        </div>
      </AuthCard>
    );
  }

  if (linkState === 'invalid') {
    return (
      <AuthCard title={title} subtitle={invalidSubtitle} footer={<AuthCardLinks />}>
        <div className="alert alert-danger" role="alert">
          {invalidMessage}
        </div>

        <div className="d-grid gap-2">
          <Link to="/auth-login" className="btn btn-primary btn-lg d-flex align-items-center justify-content-center">
            <SpriteIcon name="log-in" className="sa-icon me-2" />
            {t('auth:Auth.BackToLogin', 'Back to Login')}
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={title} subtitle={subtitle} footer={<AuthCardLinks />}>
      <AuthErrorSummary errors={formErrors} />

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          {...register('newPassword')}
          label={t('auth:Auth.NewPassword', 'New Password')}
          type="password"
          large
          autoComplete="new-password"
          autoFocus
          placeholder={newPasswordPlaceholder}
          error={errors.newPassword?.message}
        />

        <AuthField
          {...register('confirmPassword')}
          label={t('auth:Auth.ConfirmPassword', 'Confirm Password')}
          type="password"
          large
          autoComplete="new-password"
          placeholder={confirmPasswordPlaceholder}
          error={errors.confirmPassword?.message}
        />

        <div className="d-grid gap-2">
          <SubmitButton
            icon={submitIcon}
            label={submitLabel}
            variant={submitVariant}
            pending={isSubmitting}
            pendingLabel={pendingLabel}
          />
        </div>
      </form>
    </AuthCard>
  );
}
